import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { fmtMY, KIND_LABEL, daysUntil } from "@/lib/date";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowLeft, Trash, UploadSimple, BellRinging, PaperclipHorizontal, FileText, PencilSimple, Sparkle, CopySimple, ShieldCheck, PaperPlaneTilt, FileArchive, Lock, UserPlus, X as XIcon, CaretUp, CaretDown, Eye } from "@phosphor-icons/react";
import ResolutionsTab from "@/pages/ResolutionsTab";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["pending", "in_progress", "submitted", "overdue"];

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [compliance, setCompliance] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const load = async () => {
    const c = await api.get(`/clients/${id}`);
    setClient(c.data);
    // Only load related data if the subscription isn't expired for non-admins
    const isAdmin = user && user.role === "admin";
    const today = new Date().toISOString().slice(0, 10);
    const expired = c.data.active_until && c.data.active_until < today;
    if (isAdmin || !expired) {
      const [comp, docs] = await Promise.all([
        api.get(`/clients/${id}/compliance`),
        api.get(`/clients/${id}/documents`),
      ]);
      setCompliance(comp.data);
      setDocuments(docs.data);
    } else {
      setCompliance([]);
      setDocuments([]);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => toast.error("Failed to load")); }, [id, user?.role]);

  const renewClient = async () => {
    setRenewing(true);
    try {
      const { data } = await api.post(`/clients/${id}/renew`);
      toast.success(`Renewed until ${fmtMY(data.active_until)}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Renewal failed");
    } finally { setRenewing(false); }
  };

  const openEdit = () => {
    setEditForm({
      ssm_no: client.ssm_no || "",
      name: client.name || "",
      incorporation_date: client.incorporation_date || "",
      financial_year_end: client.financial_year_end || "",
      address: client.address || "",
      registered_address: client.registered_address || "",
      contact_person: client.contact_person || "",
      contact_email: client.contact_email || "",
      contact_phone: client.contact_phone || "",
      notes: client.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.ssm_no.trim() || !editForm.name.trim()) return toast.error("SSM No. and Name are required");
    setSavingEdit(true);
    try {
      await api.put(`/clients/${id}`, {
        ...editForm,
        directors: client.directors || [],
        shareholders: client.shareholders || [],
        secretaries: client.secretaries || [],
      });
      toast.success("Client updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSavingEdit(false); }
  };

  if (!client) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  // Subscription gate: if expired and current user is NOT admin, show a Renew CTA only.
  const isAdmin = user && user.role === "admin";
  const isOwner = isAdmin || (user && client.owner_user_id === user.id);
  const canWrite = isOwner;
  const todayStr = new Date().toISOString().slice(0, 10);
  const isExpired = !!client.active_until && client.active_until < todayStr;
  const availableCredits = user ? Number(user.credits || 0) : 0;

  if (isExpired && !isAdmin) {
    return (
      <div className="p-10 max-w-[900px]">
        <Link to="/clients" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-4">
          <ArrowLeft size={14} weight="bold" /> Back to Clients
        </Link>
        <div className="border border-[#FCA5A5] bg-[#FEF2F2] p-10 rounded-sm" data-testid="renew-gate">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#DA1E28] text-white rounded-sm">
              <Lock size={22} weight="bold" />
            </div>
            <div className="flex-1">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#DA1E28]">Subscription expired</div>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-[#0F172A]">{client.name}</h1>
              <p className="mt-4 text-sm text-[#334155] leading-relaxed">
                Access to this company&apos;s records has been paused because its 1&#8209;month
                access period ended on <b className="font-mono">{fmtMY(client.active_until)}</b>.
                Renew for another month to view and edit compliance data, directors, CDD, resolutions and documents.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE] h-10 px-5"
                        onClick={renewClient} disabled={renewing || availableCredits <= 0}
                        data-testid="renew-continue-btn">
                  {renewing ? "Renewing…" : "Renew for 1 credit"}
                </Button>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  You have <b className="text-foreground">{availableCredits}</b> credit(s)
                </div>
              </div>
              {availableCredits <= 0 && (
                <div className="mt-4 text-xs text-[#DA1E28]">
                  You have 0 credits. Contact your admin to top up before renewing.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-10 max-w-[1400px]">
      <Link to="/clients" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-4">
        <ArrowLeft size={14} weight="bold" /> Back to Clients
      </Link>

      <header className="mb-8 border-b border-border pb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              SSM {client.ssm_no}
            </div>
            <h1 className="mt-2 text-4xl font-semibold tracking-tight">{client.name}</h1>
            <div className="mt-3 text-sm text-muted-foreground grid grid-cols-2 md:grid-cols-4 gap-4 font-mono">
              <Meta k="Incorporation" v={fmtMY(client.incorporation_date)} />
              <Meta k="Fin. Year End" v={client.financial_year_end || "—"} />
              <Meta k="Contact" v={client.contact_person || "—"} />
              <Meta k="Email" v={client.contact_email || "—"} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ShareClientButton clientId={id} client={client} isOwner={isOwner} />
            {canWrite && (
              <>
                <Button variant="outline" className="rounded-sm" onClick={openEdit} data-testid="edit-client-btn">
                  <PencilSimple size={16} weight="bold" className="mr-2" /> Edit
                </Button>
                <Button variant="outline" className="rounded-sm" data-testid="delete-client-btn"
                        onClick={async () => {
                          if (!window.confirm("Delete this client and all its records?")) return;
                          await api.delete(`/clients/${id}`);
                          toast.success("Deleted"); nav("/clients");
                        }}>
                  <Trash size={16} weight="bold" className="mr-2" /> Delete
                </Button>
              </>
            )}
          </div>
        </div>
        {!canWrite && (
          <div className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#EFF6FF] border border-[#BFDBFE] rounded-sm text-xs text-[#1E40AF]"
               data-testid="readonly-banner">
            <Lock size={14} weight="duotone" />
            Read-only view — this company was shared with you. Contact the owner to make changes.
          </div>
        )}
      </header>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Client</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-4">
              <F label="SSM No. *"><Input value={editForm.ssm_no} onChange={e => setEditForm({...editForm, ssm_no: e.target.value})} data-testid="edit-client-ssm-input" /></F>
              <F label="Company Name *"><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-client-name-input" /></F>
              <F label="Incorporation Date (YYYY-MM-DD)"><Input value={editForm.incorporation_date} onChange={e => setEditForm({...editForm, incorporation_date: e.target.value})} placeholder="2020-01-15" data-testid="edit-client-incorp-input" /></F>
              <F label="Financial Year End (MM-DD)"><Input value={editForm.financial_year_end} onChange={e => setEditForm({...editForm, financial_year_end: e.target.value})} placeholder="12-31" data-testid="edit-client-fye-input" /></F>
              <F label="Contact Person"><Input value={editForm.contact_person} onChange={e => setEditForm({...editForm, contact_person: e.target.value})} /></F>
              <F label="Contact Email"><Input value={editForm.contact_email} onChange={e => setEditForm({...editForm, contact_email: e.target.value})} /></F>
              <F label="Contact Phone"><Input value={editForm.contact_phone} onChange={e => setEditForm({...editForm, contact_phone: e.target.value})} /></F>
              <div />
              <F label="Business Address" full>
                <Textarea rows={2} value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} />
              </F>
              <F label="Registered Office Address" full>
                <Textarea rows={2} value={editForm.registered_address || ""}
                          onChange={e => setEditForm({...editForm, registered_address: e.target.value})}
                          placeholder="As per SSM records"
                          data-testid="edit-client-registered-address-input" />
              </F>
              <F label="Notes" full>
                <Textarea rows={2} value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} />
              </F>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" data-testid="save-edit-client-btn">
              {savingEdit ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="compliance">
        <TabsList className="rounded-none bg-transparent p-0 border-b border-border w-full justify-start gap-6">
          <TabT value="compliance">Compliance</TabT>
          <TabT value="directors">Directors & Shareholders</TabT>
          <TabT value="cdd">CDD</TabT>
          <TabT value="ekyc">eKYC</TabT>
          <TabT value="resolutions">Resolutions</TabT>
          <TabT value="forms-letters">Forms & Letters</TabT>
          <TabT value="documents">Documents</TabT>
        </TabsList>

        <TabsContent value="compliance" className="pt-6">
          <ComplianceTab clientId={id} compliance={compliance} reload={load} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="directors" className="pt-6">
          <PartiesTab client={client} onSaved={load} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="cdd" className="pt-6">
          <CddTab client={client} onSaved={load} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="ekyc" className="pt-6">
          <EkycTab client={client} onSaved={load} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="resolutions" className="pt-6">
          <ResolutionsTab client={client} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="forms-letters" className="pt-6">
          <ResolutionsTab
            client={client}
            canWrite={canWrite}
            categories={["form", "letter"]}
            label="Forms & Letters"
            newLabel="Upload file"
            emptyHint="Upload a PDF from your computer and send it to signatories for e-signature."
          />
        </TabsContent>
        <TabsContent value="documents" className="pt-6">
          <DocumentsTab clientId={id} documents={documents} reload={load} canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Meta = ({ k, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
    <div className="text-sm text-foreground">{v}</div>
  </div>
);

function ShareClientButton({ clientId, client, isOwner }) {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/shares`);
      setShares(data || []);
    } catch { setShares([]); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open, clientId]);

  const addShare = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/shares`, { email: email.trim() });
      toast.success(`Shared with ${data.shared_with.email}`);
      setEmail("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Share failed");
    } finally { setAdding(false); }
  };
  const revoke = async (uid) => {
    if (!window.confirm("Remove access for this user?")) return;
    try {
      await api.delete(`/clients/${clientId}/shares/${uid}`);
      toast.success("Access removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const shareCount = (client.shared_with || []).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm" data-testid="share-client-btn">
          <UserPlus size={16} weight="bold" className="mr-2" />
          Share{shareCount > 0 ? ` (${shareCount})` : ""}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-sm">
        <DialogHeader>
          <DialogTitle>Share “{client.name}”</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Enter the email of another registered user. They will get full access to this company&apos;s
          compliance, directors, CDD, eKYC, resolutions and documents. Only the owner can add or
          remove share access.
        </p>
        {isOwner ? (
          <div className="flex items-center gap-2 mt-3">
            <Input placeholder="user@example.com" value={email}
                   onChange={e => setEmail(e.target.value)} className="rounded-sm h-9"
                   data-testid="share-email-input" />
            <Button onClick={addShare} disabled={adding || !email.trim()}
                    className="rounded-sm h-9 bg-[#0F62FE] hover:bg-[#0043CE]"
                    data-testid="share-add-btn">
              {adding ? "Sharing…" : "Share"}
            </Button>
          </div>
        ) : (
          <div className="mt-3 text-xs text-muted-foreground border border-dashed border-border p-3 rounded-sm">
            Only the owner of this company can add or remove people. Ask the owner to share it with you.
          </div>
        )}

        <div className="mt-4 border-t border-border pt-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Currently shared with
          </div>
          {shares.length === 0 ? (
            <div className="text-xs text-muted-foreground">No one else has access yet.</div>
          ) : (
            <div className="flex flex-col gap-2" data-testid="share-list">
              {shares.map(s => (
                <div key={s.id} className="flex items-center justify-between text-sm border border-border rounded-sm px-3 py-2"
                     data-testid={`share-row-${s.id}`}>
                  <div>
                    <div className="font-medium">{s.name || s.email}</div>
                    <div className="text-[11px] font-mono text-muted-foreground">{s.email}</div>
                  </div>
                  {isOwner && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#DA1E28]"
                            onClick={() => revoke(s.id)} data-testid={`share-remove-${s.id}`}>
                      <XIcon size={14} weight="bold" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TabT = ({ value, children }) => (
  <TabsTrigger value={value}
    data-testid={`tab-${value}`}
    className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-0 pb-3 font-mono text-xs uppercase tracking-widest">
    {children}
  </TabsTrigger>
);

// ----------------- Compliance Tab -----------------
const emptyComp = {
  financial_year: String(new Date().getFullYear()),
  ar_due_date: "", audit_submission_due_date: "", audited_report_due_date: "",
  ar_status: "pending", audit_status: "pending", audited_report_status: "pending",
  ar_submitted_date: "", audit_submitted_date: "", audited_report_submitted_date: "",
  notes: "",
};

function ComplianceTab({ clientId, compliance, reload, canWrite = true }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // full record or null
  const [form, setForm] = useState(emptyComp);
  const [preview, setPreview] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genYear, setGenYear] = useState(String(new Date().getFullYear()));
  const [genYears, setGenYears] = useState(1); // 1 = just this FY, 3 = this + next 2
  const [genPreview, setGenPreview] = useState(null);
  const [generating, setGenerating] = useState(false);

  const start = (rec) => {
    if (rec) { setEditing(rec); setForm({ ...emptyComp, ...rec }); }
    else { setEditing(null); setForm(emptyComp); }
    setPreview(null);
    setOpen(true);
  };

  // Fetch computed deadlines when FY changes (only on create, not edit)
  useEffect(() => {
    if (editing) return;
    const fy = (form.financial_year || "").trim();
    if (!/^\d{4}$/.test(fy)) { setPreview(null); return; }
    let cancel = false;
    api.get(`/clients/${clientId}/compliance/preview?financial_year=${fy}`)
      .then(r => { if (!cancel) setPreview(r.data); })
      .catch(() => { if (!cancel) setPreview(null); });
    return () => { cancel = true; };
  }, [form.financial_year, clientId, editing]);

  const applyPreview = () => {
    if (!preview) return;
    setForm(f => ({
      ...f,
      ar_due_date: preview.ar_due_date || f.ar_due_date,
      audit_submission_due_date: preview.audit_submission_due_date || f.audit_submission_due_date,
      audited_report_due_date: preview.audited_report_due_date || f.audited_report_due_date,
    }));
    toast.success("Statutory dates filled");
  };

  const save = async () => {
    if (!form.financial_year) return toast.error("Financial year required");
    try {
      if (editing) await api.put(`/compliance/${editing.id}`, form);
      else await api.post(`/clients/${clientId}/compliance`, form);
      toast.success("Saved"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Delete FY ${rec.financial_year} record?`)) return;
    await api.delete(`/compliance/${rec.id}`); reload();
  };

  const sendReminder = async (rec, kind) => {
    try {
      const { data } = await api.post(`/reminders/send`, { compliance_id: rec.id, kind });
      if (data.sent_count > 0) {
        toast.success(`Email sent to ${data.sent_count} recipient(s): ${data.recipients.join(", ")}`);
      } else if (data.recipients.length === 0) {
        toast.warning("No director emails on file — add director email first");
      } else {
        toast.error(`Failed to send to: ${data.failed.join(", ")}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send reminder");
    }
  };

  // ---- Auto-generate deadlines for this client ----
  useEffect(() => {
    if (!genOpen) return;
    const fy = (genYear || "").trim();
    if (!/^\d{4}$/.test(fy)) { setGenPreview(null); return; }
    let cancel = false;
    api.get(`/clients/${clientId}/compliance/preview?financial_year=${fy}`)
      .then(r => { if (!cancel) setGenPreview(r.data); })
      .catch(() => { if (!cancel) setGenPreview(null); });
    return () => { cancel = true; };
  }, [genOpen, genYear, clientId]);

  const runAutoGenerate = async () => {
    const startYear = parseInt(genYear, 10);
    if (!startYear || startYear < 1900) return toast.error("Enter a valid financial year");
    const count = Math.max(1, Math.min(10, parseInt(genYears, 10) || 1));
    setGenerating(true);
    let created = 0, skipped = 0, failed = 0;
    try {
      for (let i = 0; i < count; i++) {
        const fy = String(startYear + i);
        // skip if a record for this FY already exists
        if (compliance.some(c => String(c.financial_year) === fy)) { skipped++; continue; }
        try {
          await api.post(`/clients/${clientId}/compliance`, {
            ...emptyComp,
            financial_year: fy,
            notes: "Auto-generated from incorporation & FYE",
          });
          created++;
        } catch (e) { failed++; }
      }
      toast.success(`${created} created${skipped ? ` · ${skipped} existed` : ""}${failed ? ` · ${failed} failed` : ""}`);
      setGenOpen(false);
      reload();
    } finally { setGenerating(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Financial Year Records · {compliance.length}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => setGenOpen(true)}
                  data-testid="client-auto-generate-btn">
            <Sparkle size={16} weight="duotone" className="mr-2" /> Auto-generate Deadlines
          </Button>
          <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={() => start(null)} data-testid="add-compliance-btn">
            <Plus size={16} weight="bold" className="mr-2" /> Add FY Record
          </Button>
        </div>
      </div>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader><DialogTitle>Auto-generate deadlines</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Computes AR (Incorp anniversary + 30 days), Audit-submit (FYE + 5 months) and
            Audited-report (FYE + 7 months) for this client.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Starting FY</Label>
              <Input value={genYear} onChange={e => setGenYear(e.target.value)}
                     className="rounded-sm mt-1" placeholder="2025"
                     data-testid="client-gen-fy-input" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Number of Years</Label>
              <Input type="number" min={1} max={10} value={genYears}
                     onChange={e => setGenYears(e.target.value)}
                     className="rounded-sm mt-1" data-testid="client-gen-count-input" />
            </div>
          </div>
          {genPreview && (
            <div className="mt-4 border border-border p-3 font-mono text-[11px] space-y-1">
              <div>AR&nbsp;&nbsp;&nbsp;→ {fmtMY(genPreview.ar_due_date) || "—"}</div>
              <div>AUDIT → {fmtMY(genPreview.audit_submission_due_date) || "—"}</div>
              <div>FILING → {fmtMY(genPreview.audited_report_due_date) || "—"}</div>
              <div className="text-muted-foreground text-[10px] mt-2">
                {(!genPreview.ar_due_date && !genPreview.audit_submission_due_date)
                  ? "Add Incorporation Date and Financial Year End on this client to compute."
                  : "Preview for the starting year. Subsequent years shift by 12 months."}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={runAutoGenerate} disabled={generating}
                    data-testid="client-gen-run-btn">
              {generating ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {compliance.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-sm text-muted-foreground">
          No records yet. Add the first FY record to start tracking AR, audit and filing deadlines.
        </div>
      ) : compliance.map(rec => (
        <div key={rec.id} className="border border-border mb-4">
          <header className="px-6 py-3 border-b border-border flex items-center justify-between">
            <div className="font-mono text-sm">FY {rec.financial_year}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={() => start(rec)} data-testid={`edit-comp-${rec.id}`}>Edit</Button>
              <Button variant="outline" size="sm" className="rounded-sm h-8 text-[#DA1E28]" onClick={() => remove(rec)}>Delete</Button>
            </div>
          </header>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border">
                <Th>Deadline</Th><Th>Anniversary</Th><Th>Due</Th><Th>Balance Days to Submit</Th><Th>Status</Th><Th>Submitted</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              <DeadlineRow rec={rec} due={rec.ar_due_date} status={rec.ar_status} submitted={rec.ar_submitted_date} kind="annual_return"
                           label={KIND_LABEL.annual_return} onRemind={() => sendReminder(rec, "annual_return")} />
              <DeadlineRow rec={rec} due={rec.audit_submission_due_date} status={rec.audit_status} submitted={rec.audit_submitted_date} kind="audit_submission"
                           label={KIND_LABEL.audit_submission} onRemind={() => sendReminder(rec, "audit_submission")} />
              <DeadlineRow rec={rec} due={rec.audited_report_due_date} status={rec.audited_report_status} submitted={rec.audited_report_submitted_date} kind="audited_report"
                           label={KIND_LABEL.audited_report} onRemind={() => sendReminder(rec, "audited_report")} />
            </tbody>
          </table>
          {rec.notes && <div className="px-6 py-3 border-t border-border text-xs text-muted-foreground">Notes: {rec.notes}</div>}
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} FY Record</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <F label="Financial Year *"><Input value={form.financial_year} onChange={e => setForm({...form, financial_year: e.target.value})} placeholder="2024" data-testid="comp-fy-input" /></F>
            <div className="flex items-end">
              {!editing && preview && (preview.ar_due_date || preview.audit_submission_due_date || preview.audited_report_due_date) && (
                <button type="button" onClick={applyPreview}
                        className="w-full h-10 rounded-sm border border-dashed border-[#0F62FE] text-[#0F62FE] font-mono text-[11px] uppercase tracking-widest hover:bg-[#0F62FE]/5"
                        data-testid="apply-statutory-btn">
                  Apply statutory dates &nbsp;→ &nbsp;
                  {fmtMY(preview.ar_due_date)} · {fmtMY(preview.audit_submission_due_date)} · {fmtMY(preview.audited_report_due_date)}
                </button>
              )}
              {!editing && form.financial_year && !preview && (
                <div className="text-[11px] text-muted-foreground font-mono">Add Incorporation & FYE on the client to auto-compute.</div>
              )}
            </div>
            <DeadlineFields prefix="ar" label="Annual Return" form={form} setForm={setForm} />
            <DeadlineFields prefix="audit" label="Accounts to Audit" form={form} setForm={setForm} statusKey="audit_status" dueKey="audit_submission_due_date" subKey="audit_submitted_date" />
            <DeadlineFields prefix="audited_report" label="Audited Report Filing" form={form} setForm={setForm} statusKey="audited_report_status" dueKey="audited_report_due_date" subKey="audited_report_submitted_date" />
            <F label="Notes" full><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" data-testid="save-comp-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeadlineFields({ prefix, label, form, setForm, statusKey, dueKey, subKey }) {
  const sKey = statusKey || `${prefix}_status`;
  const dKey = dueKey || `${prefix}_due_date`;
  const bKey = subKey || `${prefix}_submitted_date`;
  return (
    <div className="col-span-2 border border-border p-4">
      <div className="font-mono text-xs uppercase tracking-widest mb-3">{label}</div>
      <div className="grid grid-cols-3 gap-3">
        <F label="Due Date (YYYY-MM-DD)"><Input value={form[dKey]} onChange={e => setForm({...form, [dKey]: e.target.value})} placeholder="2025-06-30" /></F>
        <F label="Status">
          <Select value={form[sKey]} onValueChange={v => setForm({...form, [sKey]: v})}>
            <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Submitted Date"><Input value={form[bKey]} onChange={e => setForm({...form, [bKey]: e.target.value})} placeholder="YYYY-MM-DD" /></F>
      </div>
    </div>
  );
}

function DeadlineRow({ rec, due, status, submitted, label, onRemind, kind }) {
  const overdue = status === "overdue" || (due && status !== "submitted" && new Date(due) < new Date(new Date().toDateString()));
  const eff = status === "submitted" ? "submitted" : (overdue ? "overdue" : status);
  const du = daysUntil(due);
  const anniv = anniversaryFor(kind, due);
  return (
    <tr className={`data-row border-b border-border ${eff === "overdue" ? "border-l-4 border-l-[#DA1E28]" : ""}`}>
      <td className="px-6 py-3">{label}</td>
      <td className="px-6 py-3 font-mono">{fmtMY(anniv)}</td>
      <td className="px-6 py-3 font-mono">{fmtMY(due)}</td>
      <td className="px-6 py-3 font-mono">
        {du == null ? (
          <span className="text-muted-foreground">—</span>
        ) : eff === "submitted" ? (
          <span className="text-[#24A148]">filed</span>
        ) : du < 0 ? (
          <span className="text-[#DA1E28] font-medium">{Math.abs(du)} days overdue</span>
        ) : du === 0 ? (
          <span className="text-[#DA1E28] font-medium">Due today</span>
        ) : (
          <span className={du <= 30 ? "text-[#F1C21B] font-medium" : "text-foreground"}>{du} days left</span>
        )}
      </td>
      <td className="px-6 py-3"><StatusBadge status={eff} /></td>
      <td className="px-6 py-3 font-mono text-xs text-muted-foreground">{submitted ? fmtMY(submitted) : "—"}</td>
      <td className="px-6 py-3 text-right">
        <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={onRemind}>
          <BellRinging size={14} weight="bold" className="mr-1.5" /> Remind
        </Button>
      </td>
    </tr>
  );
}

function anniversaryFor(kind, dueIso) {
  if (!dueIso) return "";
  const d = new Date(dueIso + (dueIso.length <= 10 ? "T00:00:00Z" : ""));
  if (isNaN(d)) return "";
  if (kind === "annual_return") {
    d.setUTCDate(d.getUTCDate() - 30);
    return d.toISOString().slice(0, 10);
  }
  // Audit / audited-report: due is END of month; anniversary = FYE (last day of month 5/7 earlier)
  const months = kind === "audit_submission" ? 5 : kind === "audited_report" ? 7 : 0;
  if (!months) return "";
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - months, 1));
  const dim = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), dim))
    .toISOString().slice(0, 10);
}

// ----------------- Directors / Shareholders / Secretaries -----------------
function PartiesTab({ client, onSaved, canWrite = true }) {
  const [dirs, setDirs] = useState(client.directors || []);
  const [shs, setShs] = useState(client.shareholders || []);
  const [secs, setSecs] = useState(client.secretaries || []);
  const [mgrs, setMgrs] = useState(client.managers || []);
  const [chgs, setChgs] = useState(client.charges || []);
  const [allots, setAllots] = useState(client.allotments || []);
  const [xfers, setXfers] = useState(client.transfers || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {
        ...client,
        directors: dirs, shareholders: shs, secretaries: secs,
        managers: mgrs, charges: chgs,
        allotments: allots, transfers: xfers,
      });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const nameKey = (s) => (s || "").trim().toLowerCase();

  const duplicateDirToSh = (dir) => {
    const key = nameKey(dir.name);
    if (!key) return toast.error("Add a name first");
    if (shs.some(s => nameKey(s.name) === key)) {
      return toast.error(`${dir.name} is already a shareholder`);
    }
    setShs([...shs, {
      name: dir.name || "",
      ic_no: dir.ic_no || "",
      shares: 0,
      share_class: "Ordinary",
      email: dir.email || "",
      phone: dir.phone || "",
      residential_address: dir.residential_address || "",
    }]);
    toast.success(`Copied ${dir.name} to Shareholders`);
  };

  const duplicateShToDir = (sh) => {
    const key = nameKey(sh.name);
    if (!key) return toast.error("Add a name first");
    if (dirs.some(d => nameKey(d.name) === key)) {
      return toast.error(`${sh.name} is already a director`);
    }
    setDirs([...dirs, {
      name: sh.name || "",
      ic_no: sh.ic_no || "",
      role: "Director",
      appointed_date: "",
      email: sh.email || "",
      phone: sh.phone || "",
      residential_address: sh.residential_address || "",
    }]);
    toast.success(`Copied ${sh.name} to Directors`);
  };

  const downloadRegisterBook = () => {
    const token = localStorage.getItem("cosec_token");
    window.open(`${API}/clients/${client.id}/register-book/pdf?auth=${token}`, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border border-dashed border-border p-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Statutory Register Book</div>
          <p className="text-sm mt-1">
            Auto-generates <b>Register of Directors, Managers and Secretaries</b> (S.58) and <b>Register of Members</b> (S.51) from the details below.
          </p>
        </div>
        <Button variant="outline" className="rounded-sm h-10" onClick={downloadRegisterBook}
                data-testid="register-book-pdf-btn">
          <FileText size={16} weight="bold" className="mr-2" /> Register Book PDF
        </Button>
      </div>
      <PartyPanel title="Directors" items={dirs} setItems={setDirs} testid="directors-panel"
        duplicateLabel="Copy to Shareholders" duplicateTestid="copy-dir-to-sh"
        onDuplicate={duplicateDirToSh}
        columns={[
          { key: "name", label: "Full Name", section: "Identity" },
          { key: "ic_no", label: "NRIC No.", section: "Identity" },
          { key: "passport_no", label: "Passport No.", section: "Identity" },
          { key: "passport_expiry", label: "Passport Expiry", type: "date", section: "Identity" },
          { key: "date_of_birth", label: "Date of Birth", type: "date", section: "Personal" },
          { key: "nationality", label: "Nationality", section: "Personal" },
          { key: "race", label: "Race", section: "Personal" },
          { key: "occupation", label: "Occupation", section: "Personal" },
          { key: "role", label: "Role", section: "Appointment" },
          { key: "appointed_date", label: "Date of Appointment", type: "date", section: "Appointment" },
          { key: "ceased_date", label: "Date Ceased", type: "date", section: "Appointment" },
          { key: "email", label: "Email", section: "Contact" },
          { key: "phone", label: "Phone", section: "Contact" },
          { key: "residential_address", label: "Residential Address", full: true, section: "Addresses" },
          { key: "service_address", label: "Service / Business Address", full: true, section: "Addresses" },
          { key: "other_directorships", label: "Other Directorships (Public Companies)", full: true, section: "Addresses" },
        ]} />
      <PartyPanel title="Shareholders" items={shs} setItems={setShs} testid="shareholders-panel"
        duplicateLabel="Copy to Directors" duplicateTestid="copy-sh-to-dir"
        onDuplicate={duplicateShToDir}
        columns={[
          { key: "name", label: "Name", section: "Identity" },
          { key: "ic_no", label: "NRIC / Reg. No.", section: "Identity" },
          { key: "passport_no", label: "Passport No.", section: "Identity" },
          { key: "passport_expiry", label: "Passport Expiry", type: "date", section: "Identity" },
          { key: "date_of_birth", label: "Date of Birth", type: "date", section: "Personal" },
          { key: "nationality", label: "Nationality", section: "Personal" },
          { key: "race", label: "Race", section: "Personal" },
          { key: "occupation", label: "Occupation", section: "Personal" },
          { key: "opening_shares", label: "Opening Balance (Shares)", type: "number", section: "Shareholding" },
          { key: "opening_date", label: "Opening Balance Date", type: "date", section: "Shareholding" },
          { key: "shares", label: "Current Total Shares", type: "number", section: "Shareholding" },
          { key: "share_class", label: "Class", section: "Shareholding" },
          { key: "appointed_date", label: "Date Entered", type: "date", section: "Shareholding" },
          { key: "ceased_date", label: "Date Ceased", type: "date", section: "Shareholding" },
          { key: "email", label: "Email", section: "Contact" },
          { key: "phone", label: "Phone", section: "Contact" },
          { key: "residential_address", label: "Address", full: true, section: "Addresses" },
        ]} />
      <PartyPanel title="Company Secretaries" items={secs} setItems={setSecs} testid="secretaries-panel"
        columns={[
          { key: "name", label: "Full Name" },
          { key: "ic_no", label: "NRIC No." },
          { key: "date_of_birth", label: "Date of Birth", type: "date" },
          { key: "occupation", label: "Occupation" },
          { key: "nationality", label: "Nationality" },
          { key: "race", label: "Race" },
          { key: "license_no", label: "License No. (MAICSA/MIA)" },
          { key: "ssm_cert_no", label: "SSM Practicing Cert No." },
          { key: "appointed_date", label: "Date of Appointment", type: "date" },
          { key: "ceased_date", label: "Date Ceased", type: "date" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
          { key: "residential_address", label: "Residential Address", full: true },
          { key: "service_address", label: "Service / Business Address", full: true },
        ]} />
      <PartyPanel title="Managers" items={mgrs} setItems={setMgrs} testid="managers-panel"
        columns={[
          { key: "name", label: "Full Name" },
          { key: "ic_no", label: "NRIC No." },
          { key: "passport_no", label: "Passport No." },
          { key: "date_of_birth", label: "Date of Birth", type: "date" },
          { key: "occupation", label: "Occupation" },
          { key: "nationality", label: "Nationality" },
          { key: "race", label: "Race" },
          { key: "appointed_date", label: "Date of Appointment", type: "date" },
          { key: "ceased_date", label: "Date Ceased", type: "date" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
          { key: "residential_address", label: "Residential Address", full: true },
          { key: "service_address", label: "Service / Business Address", full: true },
        ]} />
      <PartyPanel title="Register of Charges" items={chgs} setItems={setChgs} testid="charges-panel"
        columns={[
          { key: "date_created", label: "Date Created", type: "date" },
          { key: "chargee_name", label: "Name of Chargee" },
          { key: "particulars", label: "Particulars of Charges" },
          { key: "amount", label: "Amount" },
          { key: "interest_rate", label: "Rate of Interest" },
          { key: "crc_number", label: "CRC Number" },
          { key: "crc_date", label: "Date of CRC", type: "date" },
          { key: "date_discharged", label: "Date Discharged", type: "date" },
          { key: "remarks", label: "Remarks", full: true },
        ]} />
      <PartyPanel title="Allotment of Shares" items={allots} setItems={setAllots} testid="allotments-panel"
        columns={[
          { key: "allotment_date", label: "Allotment Date", type: "date" },
          { key: "allottee_name", label: "Allottee (Shareholder)", type: "select",
            options: shs.map(s => ({ value: s.name, label: `${s.name} · ${s.ic_no || s.passport_no || ""}` })) },
          { key: "ic_no", label: "NRIC / Reg. No." },
          { key: "shares", label: "No. of Shares", type: "number" },
          { key: "share_class", label: "Class" },
          { key: "consideration", label: "Consideration (e.g. RM 50,000 cash)" },
          { key: "certificate_no", label: "Certificate No." },
          { key: "remarks", label: "Remarks", full: true },
        ]} />
      <PartyPanel title="Transfer of Shares" items={xfers} setItems={setXfers} testid="transfers-panel"
        columns={[
          { key: "transfer_date", label: "Transfer Date", type: "date" },
          { key: "transferor_name", label: "Transferor (Shareholder)", type: "select",
            options: shs.map(s => ({ value: s.name, label: `${s.name} · ${s.ic_no || s.passport_no || ""}` })) },
          { key: "transferee_name", label: "Transferee (Shareholder)", type: "select",
            options: shs.map(s => ({ value: s.name, label: `${s.name} · ${s.ic_no || s.passport_no || ""}` })) },
          { key: "shares", label: "No. of Shares", type: "number" },
          { key: "share_class", label: "Class" },
          { key: "consideration", label: "Consideration" },
          { key: "instrument_stamp_date", label: "Instrument Stamped On", type: "date" },
          { key: "remarks", label: "Remarks", full: true },
        ]} />
      <ShareBalanceSummary shareholders={shs} allotments={allots} transfers={xfers} />
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !canWrite}
                className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]"
                title={!canWrite ? "Read-only — shared client" : undefined}
                data-testid="save-parties-btn">
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

function PartyPanel({ title, items, setItems, columns, testid, headerExtra, onDuplicate, duplicateLabel, duplicateTestid }) {
  const add = () => setItems([...items, Object.fromEntries(columns.map(c => [c.key, ""]))]);
  const update = (i, k, v) => { const n = [...items]; n[i] = { ...n[i], [k]: v }; setItems(n); };
  const remove = (i) => setItems(items.filter((_, x) => x !== i));

  // Group columns by their `section` label so each person's card renders as
  // labelled sub-blocks (Identity, Personal, Contact, Address, …). Columns
  // without a section fall back to a shared "Details" group.
  const grouped = (() => {
    const map = new Map();
    for (const c of columns) {
      const s = c.section || "Details";
      if (!map.has(s)) map.set(s, []);
      map.get(s).push(c);
    }
    return Array.from(map.entries());
  })();

  return (
    <div className="border border-border rounded-sm bg-white overflow-hidden" data-testid={testid}>
      <header className="flex items-center justify-between px-6 py-3 border-b border-border bg-muted/30">
        <span className="font-mono text-xs uppercase tracking-widest">{title} · {items.length}</span>
        <div className="flex items-center gap-2">
          {headerExtra}
          <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={add}
                  data-testid={`${testid}-add-btn`}>
            <Plus size={14} weight="bold" className="mr-1" />Add
          </Button>
        </div>
      </header>
      {items.length === 0 && (
        <div className="p-8 text-sm text-muted-foreground text-center">
          None added. Click <b>Add</b> to record the first entry.
        </div>
      )}
      {items.map((it, i) => (
        <div key={i} className="border-b border-border last:border-b-0"
             data-testid={`${testid}-row-${i}`}>
          {/* Card header: index + name preview + row actions */}
          <div className="flex items-center justify-between gap-3 px-6 py-2.5 bg-[#F9FAFB] border-b border-border/60">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full bg-[#111827] text-white font-mono text-[11px]">
                #{i + 1}
              </span>
              <span className="text-sm font-medium text-[#111827] truncate">
                {it.name || <span className="text-muted-foreground italic">Untitled</span>}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onDuplicate && (
                <Button size="sm" variant="ghost" className="rounded-sm h-7 px-2 text-xs"
                        data-testid={duplicateTestid ? `${duplicateTestid}-${i}` : undefined}
                        onClick={() => onDuplicate(it)}>
                  <CopySimple size={13} weight="bold" className="mr-1" />
                  {duplicateLabel || "Duplicate"}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => remove(i)}
                      className="rounded-sm h-7 px-2 text-xs text-[#DA1E28] hover:bg-[#FEE2E2]">
                <Trash size={13} weight="bold" className="mr-1" />Remove
              </Button>
            </div>
          </div>
          {/* Grouped field sections */}
          <div className="px-6 py-4 space-y-5">
            {grouped.map(([section, cols]) => (
              <div key={section}>
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 pb-1 border-b border-dashed border-border">
                  {section}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {cols.map(c => (
                    <div key={c.key} className={c.full ? "col-span-2" : ""}>
                      <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</Label>
                      {c.type === "select" ? (
                        <select
                          className="mt-1 rounded-sm w-full h-9 border border-input bg-background px-3 text-sm"
                          value={it[c.key] || ""}
                          onChange={e => update(i, c.key, e.target.value)}>
                          <option value="">— Select —</option>
                          {(c.options || []).map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                          {it[c.key] && !(c.options || []).some(o => o.value === it[c.key]) && (
                            <option value={it[c.key]}>{it[c.key]} (manual)</option>
                          )}
                        </select>
                      ) : (
                        <Input className="mt-1 rounded-sm" type={c.type || "text"}
                               value={it[c.key] || ""} onChange={e => update(i, c.key, e.target.value)} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Compute a running balance per shareholder from allotments (acquired) and
// transfers (in/out). Shown below the transfer panel so drafters can see how
// many shares each member actually holds after every movement.
function ShareBalanceSummary({ shareholders, allotments, transfers }) {
  if (!shareholders || shareholders.length === 0) return null;
  const norm = (s) => (s || "").trim().toLowerCase();
  const rows = shareholders.map((sh) => {
    const key = norm(sh.name);
    const openingRaw = parseInt(sh.opening_shares, 10) || 0;
    const declared = parseInt(sh.shares, 10) || 0;
    // If no explicit Opening Balance was entered, fall back to the "Current Total
    // Shares" (declared subscription at incorporation) so that the running
    // balance stays in sync with the shareholder's actual holding.
    const opening = openingRaw > 0 ? openingRaw : declared;
    const openingFallback = openingRaw === 0 && declared > 0;
    const allotted = (allotments || [])
      .filter(a => norm(a.allottee_name) === key)
      .reduce((n, a) => n + (parseInt(a.shares, 10) || 0), 0);
    const trIn = (transfers || [])
      .filter(t => norm(t.transferee_name) === key)
      .reduce((n, t) => n + (parseInt(t.shares, 10) || 0), 0);
    const trOut = (transfers || [])
      .filter(t => norm(t.transferor_name) === key)
      .reduce((n, t) => n + (parseInt(t.shares, 10) || 0), 0);
    const balance = opening + allotted + trIn - trOut;
    // Only flag a mismatch when the user explicitly set an opening balance and
    // it doesn't reconcile with declared. Fallback-based rows are considered OK.
    const mismatch = !openingFallback && balance !== declared;
    return { name: sh.name || "—", declared, opening, openingFallback, allotted, trIn, trOut, balance, mismatch };
  });
  const totalDeclared = rows.reduce((n, r) => n + r.declared, 0);
  const totalBalance = rows.reduce((n, r) => n + r.balance, 0);
  return (
    <div className="border border-border" data-testid="share-balance-summary">
      <div className="px-4 py-3 border-b border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Share balance summary
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border bg-muted/30">
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Shareholder</th>
              <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Declared</th>
              <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Opening</th>
              <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Allotted</th>
              <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Trf In</th>
              <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Trf Out</th>
              <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Balance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/60">
                <td className="px-4 py-2">{r.name}</td>
                <td className="px-3 py-2 text-right font-mono">{r.declared.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono" title={r.openingFallback ? "Auto-filled from Current Total Shares. Set an Opening Balance explicitly to override." : ""}>
                  {r.opening ? r.opening.toLocaleString() : "—"}
                  {r.openingFallback ? <span className="text-[10px] text-muted-foreground ml-1">(auto)</span> : null}
                </td>
                <td className="px-3 py-2 text-right font-mono">{r.allotted.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono text-[#24A148]">{r.trIn ? `+${r.trIn.toLocaleString()}` : "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-[#DA1E28]">{r.trOut ? `-${r.trOut.toLocaleString()}` : "—"}</td>
                <td className={`px-3 py-2 text-right font-mono font-bold ${r.mismatch ? "text-[#DA1E28]" : "text-[#24A148]"}`}
                    title={r.mismatch ? `Declared (${r.declared}) does not match computed balance (${r.balance}). Add an Opening Balance on the shareholder row to reconcile.` : "Matches declared"}>
                  {r.balance.toLocaleString()}
                  {r.mismatch && " ⚠"}
                </td>
              </tr>
            ))}
            <tr className="bg-muted/20 font-bold">
              <td className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Total</td>
              <td className="px-3 py-2 text-right font-mono">{totalDeclared.toLocaleString()}</td>
              <td colSpan={4}></td>
              <td className="px-3 py-2 text-right font-mono text-foreground">
                {totalBalance.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground">
        Balance = Opening + Allotted + Transferred In − Transferred Out. When <b>Opening Balance</b> is blank, <b>Current Total Shares</b> is treated as the shareholder's opening subscription at incorporation.
      </div>
    </div>
  );
}

// ----------------- Documents -----------------
function DocumentsTab({ clientId, documents, reload, canWrite = true }) {
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [category, setCategory] = useState("general");
  const [uploading, setUploading] = useState(false);
  // Password-gated delete
  const [confirmDoc, setConfirmDoc] = useState(null); // doc row or null
  const [confirmPw, setConfirmPw] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    setUploading(true);
    const token = localStorage.getItem("cosec_token");
    let ok = 0, failed = 0;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        const r = await fetch(`${API}/clients/${clientId}/documents`, {
          method: "POST", body: fd,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) ok++; else failed++;
      }
      if (ok) toast.success(`${ok} file(s) uploaded${failed ? ` · ${failed} failed` : ""}`);
      if (!ok && failed) toast.error(`${failed} file(s) failed to upload`);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const changeCategory = async (docId, cat) => {
    try {
      await api.patch(`/documents/${docId}`, { category: cat });
      toast.success("Category updated");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update category");
    }
  };

  const reorder = async (docId, direction) => {
    try {
      await api.post(`/documents/${docId}/reorder`, { direction });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reorder");
    }
  };

  // Rename support
  const [renameDoc, setRenameDoc] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const openRename = (doc) => {
    setRenameDoc(doc);
    setRenameVal(doc.original_filename || "");
  };
  const doRename = async () => {
    if (!renameDoc) return;
    const name = (renameVal || "").trim();
    if (!name) return toast.error("Filename cannot be empty");
    if (name === renameDoc.original_filename) { setRenameDoc(null); return; }
    setRenameBusy(true);
    try {
      await api.patch(`/documents/${renameDoc.id}`, { original_filename: name });
      toast.success("Renamed");
      setRenameDoc(null);
      setRenameVal("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to rename");
    } finally { setRenameBusy(false); }
  };

  const askDelete = (doc) => {
    setConfirmDoc(doc);
    setConfirmPw("");
  };

  const doDelete = async () => {
    if (!confirmDoc) return;
    if (!confirmPw) return toast.error("Password required");
    setConfirmBusy(true);
    try {
      await api.post("/auth/verify-password", { password: confirmPw });
      await api.delete(`/documents/${confirmDoc.id}`);
      toast.success("Document deleted");
      setConfirmDoc(null);
      setConfirmPw("");
      reload();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Incorrect password");
    } finally { setConfirmBusy(false); }
  };

  const download = (doc) => {
    const token = localStorage.getItem("cosec_token");
    window.open(`${API}/documents/${doc.id}/download?auth=${token}`, "_blank");
  };

  const downloadAllZip = () => {
    if (!documents || documents.length === 0) {
      toast.error("No documents to download");
      return;
    }
    const token = localStorage.getItem("cosec_token");
    window.open(`${API}/clients/${clientId}/documents/zip?auth=${token}`, "_blank");
  };

  return (
    <div>
      {canWrite && (
      <div className="border-2 border-dashed border-border p-8 mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest mb-2 flex items-center gap-2">
            <PaperclipHorizontal size={16} weight="duotone" /> Upload Document
          </div>
          <p className="text-sm text-muted-foreground">
            SSM certs, audited reports, board minutes — PDFs and images.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1 rounded-sm w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="ssm">SSM Certificate</SelectItem>
                <SelectItem value="audited_report">Audited Report</SelectItem>
                <SelectItem value="annual_return">Annual Return</SelectItem>
                <SelectItem value="minutes">Board Minutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <input ref={inputRef} type="file" hidden multiple onChange={e => upload(e.target.files)} data-testid="file-input" />
          <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-btn">
            <UploadSimple size={16} weight="bold" className="mr-2" /> {uploading ? "Uploading…" : "Choose files"}
          </Button>
        </div>
      </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          {documents.length} document{documents.length === 1 ? "" : "s"}
        </span>
        <Button variant="outline" className="rounded-sm h-9"
                onClick={downloadAllZip} disabled={documents.length === 0}
                data-testid="download-docs-zip-btn">
          <FileArchive size={16} weight="duotone" className="mr-2" />
          Download all as ZIP
        </Button>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm table-fixed">
          <colgroup>
            <col className="w-[46%]" />
            <col className="w-[180px]" />
            <col className="w-[80px]" />
            <col className="w-[110px]" />
            <col className="w-[140px]" />
          </colgroup>
          <thead>
            <tr className="text-left border-b border-border">
              <Th>File</Th><Th>Category</Th><Th>Size</Th><Th>Uploaded</Th><Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {documents.length === 0 && <tr><td colSpan={5} className="px-6 py-8 text-sm text-muted-foreground">No documents yet.</td></tr>}
            {documents.map((d, i) => (
              <tr key={d.id} className="data-row border-b border-border">
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {canWrite && (
                      <div className="flex flex-col -my-1 shrink-0">
                        <button type="button"
                                onClick={() => reorder(d.id, "up")}
                                disabled={i === 0}
                                className="p-0.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-[#6B7280]"
                                title="Move up"
                                data-testid={`doc-up-${d.id}`}>
                          <CaretUp size={12} weight="bold" />
                        </button>
                        <button type="button"
                                onClick={() => reorder(d.id, "down")}
                                disabled={i === documents.length - 1}
                                className="p-0.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-[#6B7280]"
                                title="Move down"
                                data-testid={`doc-down-${d.id}`}>
                          <CaretDown size={12} weight="bold" />
                        </button>
                      </div>
                    )}
                    <FileText size={16} weight="duotone" className="shrink-0" />
                    <span className="truncate" title={d.original_filename}>{d.original_filename}</span>
                  </div>
                </td>
                <td className="px-6 py-3">
                  <Select value={d.category || "general"} onValueChange={(v) => changeCategory(d.id, v)} disabled={!canWrite}>
                    <SelectTrigger className="h-8 rounded-sm w-full font-mono text-xs uppercase"
                                   data-testid={`doc-category-${d.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="ssm">SSM Certificate</SelectItem>
                      <SelectItem value="audited_report">Audited Report</SelectItem>
                      <SelectItem value="annual_return">Annual Return</SelectItem>
                      <SelectItem value="minutes">Board Minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-6 py-3 font-mono text-xs">{Math.round((d.size || 0) / 1024)} KB</td>
                <td className="px-6 py-3 font-mono text-xs">{fmtMY(d.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    <button type="button" onClick={() => download(d)}
                            className="p-1.5 rounded hover:bg-muted text-[#374151]"
                            title="View / download">
                      <Eye size={16} weight="bold" />
                    </button>
                    {canWrite && (
                      <>
                        <button type="button" onClick={() => openRename(d)}
                                className="p-1.5 rounded hover:bg-muted text-[#374151]"
                                title="Rename"
                                data-testid={`doc-rename-${d.id}`}>
                          <PencilSimple size={16} weight="bold" />
                        </button>
                        <button type="button" onClick={() => askDelete(d)}
                                className="p-1.5 rounded hover:bg-[#FEE2E2] text-[#DA1E28]"
                                title="Delete">
                          <Trash size={16} weight="bold" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!renameDoc} onOpenChange={(o) => { if (!o) { setRenameDoc(null); setRenameVal(""); } }}>
        <DialogContent className="max-w-md rounded-lg" data-testid="rename-doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={18} weight="duotone" className="text-[#1E6BC6]" />
              Rename document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">New filename</Label>
            <Input value={renameVal} onChange={e => setRenameVal(e.target.value)}
                   autoFocus className="rounded-md"
                   onKeyDown={e => { if (e.key === "Enter") doRename(); }}
                   data-testid="rename-doc-input" />
            <p className="text-[11px] text-muted-foreground">Include the file extension (e.g. <code>.pdf</code>). This is a display name; the stored file is untouched.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md"
                    onClick={() => { setRenameDoc(null); setRenameVal(""); }}>Cancel</Button>
            <Button className="rounded-md bg-[#0F62FE] hover:bg-[#0043CE] text-white"
                    onClick={doRename} disabled={renameBusy || !renameVal.trim()}
                    data-testid="rename-doc-save-btn">
              {renameBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDoc} onOpenChange={(o) => { if (!o) { setConfirmDoc(null); setConfirmPw(""); } }}>
        <DialogContent className="max-w-sm rounded-lg" data-testid="delete-doc-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={18} weight="duotone" className="text-[#DA1E28]" />
              Confirm delete
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              You are about to permanently delete
              <b className="text-[#111827]"> {confirmDoc?.original_filename}</b>.
              Enter your password to confirm.
            </p>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Signed in as</Label>
              <Input value={user?.email || ""} readOnly className="mt-1 rounded-md bg-muted/40" data-testid="delete-doc-email" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                     autoFocus className="mt-1 rounded-md"
                     onKeyDown={e => { if (e.key === "Enter") doDelete(); }}
                     data-testid="delete-doc-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md"
                    onClick={() => { setConfirmDoc(null); setConfirmPw(""); }}
                    data-testid="delete-doc-cancel">Cancel</Button>
            <Button className="rounded-md bg-[#DA1E28] hover:bg-[#B81922] text-white"
                    onClick={doDelete} disabled={confirmBusy || !confirmPw}
                    data-testid="delete-doc-confirm-btn">
              {confirmBusy ? "Verifying…" : "Delete document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function CddTab({ client, onSaved, canWrite = true }) {
  return (
    <div className="space-y-6">
      <AmlaPanel client={client} onSaved={onSaved} canWrite={canWrite} />
      <CddPersonsPanel client={client} onSaved={onSaved} canWrite={canWrite} />
      <CddForm client={client} onSaved={onSaved} canWrite={canWrite} />
      <div className="text-xs text-muted-foreground leading-relaxed border border-dashed border-border p-4">
        <b>Note:</b> The <b>AMLA CDD PDF</b> button above compiles the client-level CDD
        register and every person's self-declaration + screening result into one filed-ready document.
      </div>
    </div>
  );
}

function CddPersonsPanel({ client, onSaved }) {
  const [sending, setSending] = useState(null);
  const persons = [
    ...(client.directors || []).map((p, i) => ({ party: "director", index: i, person: p, role: p.role || "Director" })),
    ...(client.shareholders || []).map((p, i) => ({ party: "shareholder", index: i, person: p, role: "Shareholder" })),
    ...(client.secretaries || []).map((p, i) => ({ party: "secretary", index: i, person: p, role: "Company Secretary" })),
  ];

  const send = async (row) => {
    if (!row.person.email) return toast.error(`${row.person.name || "This person"} has no email on file`);
    const key = `${row.party}-${row.index}`;
    setSending(key);
    try {
      const { data } = await api.post(`/clients/${client.id}/cdd/send-form`, {
        party: row.party, index: row.index,
      });
      toast.success(`CDD form link sent to ${data.recipient}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally { setSending(null); }
  };

  const statusMeta = (row) => {
    const d = row.person.cdd_declaration;
    if (d?.submitted_at) return { label: "Submitted", css: "bg-[#24A148] text-white", at: d.submitted_at };
    return { label: "Not sent", css: "bg-[#E0E0E0] text-[#404040]", at: null };
  };

  return (
    <div className="border border-border" data-testid="cdd-persons">
      <header className="px-6 py-3 border-b border-border">
        <span className="font-mono text-xs uppercase tracking-widest">Per-person CDD self-declaration</span>
        <p className="text-xs text-muted-foreground mt-1">Send each shareholder/director/secretary a link to complete their own CDD form covering identity, beneficial ownership, source of wealth, PEP + sanctions self-declaration.</p>
      </header>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-border">
            <Th>Name</Th><Th>Role</Th><Th>Email</Th><Th>Status</Th><Th>Submitted</Th><Th className="text-right">Action</Th>
          </tr>
        </thead>
        <tbody>
          {persons.length === 0 && (
            <tr><td colSpan={6} className="px-6 py-8 text-sm text-muted-foreground">
              Add directors, shareholders or secretaries first.
            </td></tr>
          )}
          {persons.map((row, i) => {
            const m = statusMeta(row);
            const key = `${row.party}-${row.index}`;
            return (
              <tr key={key} className="data-row border-b border-border">
                <td className="px-6 py-3">{row.person.name || "—"}</td>
                <td className="px-6 py-3 text-xs">{row.role}</td>
                <td className="px-6 py-3 text-xs">{row.person.email || "—"}</td>
                <td className="px-6 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest ${m.css}`}>
                    {m.label}
                  </span>
                </td>
                <td className="px-6 py-3 font-mono text-xs">{m.at ? fmtMY(m.at) : "—"}</td>
                <td className="px-6 py-3 text-right">
                  <Button size="sm" variant="outline" className="rounded-sm h-8"
                          disabled={sending === key || !row.person.email}
                          onClick={() => send(row)}
                          data-testid={`cdd-send-${i}`}>
                    <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
                    {sending === key ? "Sending…" : (m.at ? "Resend" : "Send form")}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SERVICES_LIST = [
  "Company incorporation",
  "Company secretarial services",
  "Registered office / correspondence address",
  "Nominee director",
  "Nominee shareholder",
  "Bookkeeping / accounting",
  "Tax filing",
  "Payroll",
  "Other",
];

const CHANNEL_OPTIONS = [
  { value: "face_to_face", label: "Face-to-face" },
  { value: "non_face_to_face", label: "Non face-to-face (online / referral)" },
];

const SOF_METHODS = [
  { value: "", label: "— Not verified —" },
  { value: "bank_statement", label: "Bank statement" },
  { value: "employment_letter", label: "Employment letter / payslip" },
  { value: "business_income", label: "Audited accounts / business income" },
  { value: "investment_returns", label: "Investment returns" },
  { value: "inheritance_gift", label: "Inheritance / gift documentation" },
  { value: "other", label: "Other (see notes)" },
];

function CddForm({ client, onSaved }) {
  const amla = client.amla || {};
  const [form, setForm] = useState({
    services_engaged: amla.services_engaged || [],
    engagement_frequency: amla.engagement_frequency || "",
    expected_annual_fee: amla.expected_annual_fee || "",
    delivery_channel: amla.delivery_channel || "",
    country_risk: amla.country_risk || "",
    client_type_risk: amla.client_type_risk || "",
    product_risk: amla.product_risk || "",
    channel_risk: amla.channel_risk || "",
    sof_description: amla.sof_description || "",
    sof_verification_method: amla.sof_verification_method || "",
    sof_evidence_obtained: !!amla.sof_evidence_obtained,
    client_is_pep: !!amla.client_is_pep,
    sanctions_hit: !!amla.sanctions_hit,
    adverse_media_findings: !!amla.adverse_media_findings,
    trigger_change_directors: !!amla.trigger_change_directors,
    trigger_new_beneficial_owner: !!amla.trigger_new_beneficial_owner,
    trigger_high_risk_transaction: !!amla.trigger_high_risk_transaction,
    monitoring_notes: amla.monitoring_notes || "",
    assessed_by: amla.assessed_by || "",
    approver: amla.approver || "",
  });
  const [saving, setSaving] = useState(false);

  const toggleService = (svc) => {
    setForm(f => ({
      ...f,
      services_engaged: f.services_engaged.includes(svc)
        ? f.services_engaged.filter(s => s !== svc)
        : [...f.services_engaged, svc],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {
        ...client,
        amla: { ...(client.amla || {}), ...form },
      });
      toast.success("CDD form saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save CDD form");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-border" data-testid="cdd-form">
      <header className="px-6 py-3 border-b border-border">
        <span className="font-mono text-xs uppercase tracking-widest">CDD Assessment Form</span>
        <p className="text-xs text-muted-foreground mt-1">Complete this DNFBP customer due diligence questionnaire in line with BNM AML/CFT + AMLATFPUAA 2001 for company secretaries.</p>
      </header>

      <div className="p-6 space-y-8">
        <Section title="Section A · Purpose & nature of engagement">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Services engaged</Label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
              {SERVICES_LIST.map(s => (
                <label key={s} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`cdd-svc-${s.slice(0,10)}`}>
                  <input type="checkbox" className="mt-1" checked={form.services_engaged.includes(s)}
                         onChange={() => toggleService(s)} />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Engagement frequency">
              <Select value={form.engagement_frequency || "__none__"}
                      onValueChange={v => setForm({ ...form, engagement_frequency: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="cdd-frequency"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not set —</SelectItem>
                  <SelectItem value="one_off">One-off</SelectItem>
                  <SelectItem value="ongoing">Ongoing / retainer</SelectItem>
                  <SelectItem value="ad_hoc">Ad-hoc</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Expected annual fee (MYR)">
              <Input value={form.expected_annual_fee}
                     onChange={e => setForm({ ...form, expected_annual_fee: e.target.value })}
                     className="rounded-sm" placeholder="e.g. 4,800"
                     data-testid="cdd-annual-fee" />
            </F>
            <F label="Delivery channel" full>
              <Select value={form.delivery_channel || "__none__"}
                      onValueChange={v => setForm({ ...form, delivery_channel: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="cdd-channel"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not set —</SelectItem>
                  {CHANNEL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>
        </Section>

        <Section title="Section B · Risk factor assessment (per BNM/AML DNFBP guidelines)">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <RiskSelect label="Country / geography" value={form.country_risk} onChange={v => setForm({ ...form, country_risk: v })} testid="cdd-country-risk" />
            <RiskSelect label="Client / entity type"  value={form.client_type_risk} onChange={v => setForm({ ...form, client_type_risk: v })} testid="cdd-client-risk" />
            <RiskSelect label="Product / service"    value={form.product_risk}    onChange={v => setForm({ ...form, product_risk: v })}    testid="cdd-product-risk" />
            <RiskSelect label="Delivery channel"     value={form.channel_risk}    onChange={v => setForm({ ...form, channel_risk: v })}    testid="cdd-channel-risk" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Set the <b>overall risk rating</b> for the client at the top of this page (Update AMLA).
            Overall rating should reflect the highest factor and any escalation.
          </p>
        </Section>

        <Section title="Section C · Source of funds / wealth">
          <F label="Description of source of funds / wealth" full>
            <Textarea rows={3} value={form.sof_description}
                      onChange={e => setForm({ ...form, sof_description: e.target.value })}
                      className="rounded-sm"
                      placeholder="e.g. Operating revenue from engineering services; owner's income from employment at ABC Sdn. Bhd."
                      data-testid="cdd-sof-desc" />
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Verification method">
              <Select value={form.sof_verification_method || ""}
                      onValueChange={v => setForm({ ...form, sof_verification_method: v })}>
                <SelectTrigger className="rounded-sm" data-testid="cdd-sof-method"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {SOF_METHODS.map(m => <SelectItem key={m.value || "none"} value={m.value || "__none__"}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Evidence obtained">
              <label className="flex items-center gap-2 text-sm h-10 cursor-pointer" data-testid="cdd-sof-evidence">
                <input type="checkbox" checked={form.sof_evidence_obtained}
                       onChange={e => setForm({ ...form, sof_evidence_obtained: e.target.checked })} />
                <span>Documentary evidence has been sighted and filed</span>
              </label>
            </F>
          </div>
        </Section>

        <Section title="Section D · Declarations">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <CheckRow field="client_is_pep" label="Client / any principal is a PEP" form={form} setForm={setForm} />
            <CheckRow field="sanctions_hit" label="Match found on any sanctions list (UN / OFAC / EU / MOHA)" form={form} setForm={setForm} />
            <CheckRow field="adverse_media_findings" label="Adverse media findings on client / principals" form={form} setForm={setForm} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            If any of the above is ticked, mark <b>EDD required</b> at the top of the page and document escalation in the notes.
          </p>
        </Section>

        <Section title="Section E · Ongoing monitoring triggers">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <CheckRow field="trigger_change_directors" label="Change in directors / secretary since last CDD" form={form} setForm={setForm} />
            <CheckRow field="trigger_new_beneficial_owner" label="New beneficial owner identified" form={form} setForm={setForm} />
            <CheckRow field="trigger_high_risk_transaction" label="High-risk transaction observed" form={form} setForm={setForm} />
          </div>
          <F label="Monitoring notes" full>
            <Textarea rows={3} value={form.monitoring_notes}
                      onChange={e => setForm({ ...form, monitoring_notes: e.target.value })}
                      className="rounded-sm"
                      placeholder="Actions taken since last review; escalations; STR filed reference"
                      data-testid="cdd-monitoring-notes" />
          </F>
        </Section>

        <Section title="Section F · Sign-off">
          <div className="grid grid-cols-2 gap-4">
            <F label="Assessed by (compliance officer)">
              <Input value={form.assessed_by} onChange={e => setForm({ ...form, assessed_by: e.target.value })}
                     className="rounded-sm" data-testid="cdd-assessed-by" />
            </F>
            <F label="Approver (required for High risk / EDD)">
              <Input value={form.approver} onChange={e => setForm({ ...form, approver: e.target.value })}
                     className="rounded-sm" data-testid="cdd-approver" />
            </F>
          </div>
        </Section>
      </div>

      <footer className="px-6 py-3 border-t border-border flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]"
                data-testid="cdd-save-btn">
          {saving ? "Saving…" : "Save CDD form"}
        </Button>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3 pb-1 border-b border-border">{title}</div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function RiskSelect({ label, value, onChange, testid }) {
  return (
    <F label={label}>
      <Select value={value || "__none__"} onValueChange={v => onChange(v === "__none__" ? "" : v)}>
        <SelectTrigger className="rounded-sm" data-testid={testid}><SelectValue placeholder="Not rated" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— Not rated —</SelectItem>
          <SelectItem value="low">Low</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="high">High</SelectItem>
        </SelectContent>
      </Select>
    </F>
  );
}

function CheckRow({ field, label, form, setForm }) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`cdd-decl-${field}`}>
      <input type="checkbox" className="mt-1" checked={!!form[field]}
             onChange={e => setForm({ ...form, [field]: e.target.checked })} />
      <span>{label}</span>
    </label>
  );
}

const RISK_TONES = {
  low:    { label: "Low risk",    css: "bg-[#24A148] text-white" },
  medium: { label: "Medium risk", css: "bg-[#F1C21B] text-[#3D3300]" },
  high:   { label: "High risk",   css: "bg-[#DA1E28] text-white" },
};

function AmlaPanel({ client, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    risk_rating: client.amla?.risk_rating || "",
    risk_assessment_date: client.amla?.risk_assessment_date || "",
    next_review_date: client.amla?.next_review_date || "",
    edd_required: !!client.amla?.edd_required,
    pep_in_structure: !!client.amla?.pep_in_structure,
    higher_risk_jurisdiction: !!client.amla?.higher_risk_jurisdiction,
    complex_ownership: !!client.amla?.complex_ownership,
    cash_intensive_business: !!client.amla?.cash_intensive_business,
    source_of_funds_verified: !!client.amla?.source_of_funds_verified,
    str_filed: !!client.amla?.str_filed,
    str_reference: client.amla?.str_reference || "",
    business_nature: client.amla?.business_nature || "",
    notes: client.amla?.notes || "",
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, { ...client, amla: { ...form } });
      toast.success("AMLA record saved");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const rating = client.amla?.risk_rating;
  const ratingBadge = rating && RISK_TONES[rating];
  const edd = !!client.amla?.edd_required;
  const flags = [
    client.amla?.pep_in_structure && "PEP in structure",
    client.amla?.higher_risk_jurisdiction && "Higher-risk jurisdiction",
    client.amla?.complex_ownership && "Complex ownership",
    client.amla?.cash_intensive_business && "Cash-intensive",
    client.amla?.str_filed && `STR filed${client.amla?.str_reference ? ` (${client.amla.str_reference})` : ""}`,
  ].filter(Boolean);

  return (
    <div className="mb-6 border border-border" data-testid="amla-panel">
      <header className="flex items-center justify-between px-6 py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs uppercase tracking-widest">AMLA / AMLATFPUAA 2001 · CDD Register</span>
          {ratingBadge && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest ${ratingBadge.css}`}
                  data-testid="amla-risk-badge">{ratingBadge.label}</span>
          )}
          {edd && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest bg-[#0F62FE] text-white">
              EDD required
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-sm h-8"
                  onClick={() => {
                    const t = localStorage.getItem("cosec_token");
                    window.open(`${API}/clients/${client.id}/amla-cdd/pdf?auth=${t}`, "_blank");
                  }}
                  data-testid="amla-cdd-pdf-btn">
            <FileText size={14} weight="bold" className="mr-1.5" /> AMLA CDD PDF
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setOpen(true)}
                  data-testid="amla-edit-btn">
            <PencilSimple size={14} weight="bold" className="mr-1.5" />
            {rating ? "Update AMLA" : "Complete AMLA CDD"}
          </Button>
        </div>
      </header>
      <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Last risk assessment</div>
          <div className="mt-1">{client.amla?.risk_assessment_date ? fmtMY(client.amla.risk_assessment_date) : "—"}</div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Next review</div>
          <div className="mt-1">{client.amla?.next_review_date ? fmtMY(client.amla.next_review_date) : "—"}</div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Source of funds verified</div>
          <div className="mt-1">{client.amla?.source_of_funds_verified ? "Yes" : "No"}</div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Risk flags</div>
          <div className="mt-1 text-xs">{flags.length ? flags.join(" · ") : "—"}</div>
        </div>
        <div className="col-span-2 md:col-span-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Nature of business</div>
          <div className="mt-1 text-xs">{client.amla?.business_nature || "—"}</div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AMLA CDD — {client.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <F label="Risk rating">
              <Select value={form.risk_rating || "__none__"} onValueChange={v => setForm({ ...form, risk_rating: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="amla-risk-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not rated</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Risk assessment date (YYYY-MM-DD)">
              <Input value={form.risk_assessment_date} onChange={e => setForm({ ...form, risk_assessment_date: e.target.value })}
                     className="rounded-sm" data-testid="amla-assessment-date" />
            </F>
            <F label="Next review date (YYYY-MM-DD)" full>
              <Input value={form.next_review_date} onChange={e => setForm({ ...form, next_review_date: e.target.value })}
                     className="rounded-sm" data-testid="amla-next-review" />
            </F>
            <F label="Nature of business relationship" full>
              <Textarea rows={2} value={form.business_nature}
                        onChange={e => setForm({ ...form, business_nature: e.target.value })}
                        className="rounded-sm"
                        placeholder="Brief description of client's business, purpose of engagement, expected services"
                        data-testid="amla-business-nature" />
            </F>
            <div className="col-span-2 border-t border-border pt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Risk flags</div>
              <div className="grid grid-cols-2 gap-2">
                <AmlaCheck field="edd_required" label="Enhanced Due Diligence (EDD) required" form={form} setForm={setForm} />
                <AmlaCheck field="pep_in_structure" label="PEP in ownership/control structure" form={form} setForm={setForm} />
                <AmlaCheck field="higher_risk_jurisdiction" label="Higher-risk jurisdiction involved (FATF-listed)" form={form} setForm={setForm} />
                <AmlaCheck field="complex_ownership" label="Complex / opaque ownership structure" form={form} setForm={setForm} />
                <AmlaCheck field="cash_intensive_business" label="Cash-intensive business" form={form} setForm={setForm} />
                <AmlaCheck field="source_of_funds_verified" label="Source of funds/wealth verified" form={form} setForm={setForm} />
              </div>
            </div>
            <div className="col-span-2 border-t border-border pt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Suspicious Transaction Report (STR to BNM/FIED)</div>
              <div className="grid grid-cols-2 gap-3">
                <AmlaCheck field="str_filed" label="STR has been filed" form={form} setForm={setForm} />
                <F label="STR / FIED reference">
                  <Input value={form.str_reference} onChange={e => setForm({ ...form, str_reference: e.target.value })}
                         className="rounded-sm" data-testid="amla-str-ref" />
                </F>
              </div>
            </div>
            <F label="AMLA notes" full>
              <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                        className="rounded-sm" data-testid="amla-notes" />
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={save} disabled={saving}
                    data-testid="amla-save-btn">
              {saving ? "Saving…" : "Save AMLA record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SanctionsChip({ label, href, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       data-testid={testid}
       className="inline-flex items-center h-7 px-2.5 border border-border rounded-sm text-[11px] font-mono hover:bg-[#0F62FE] hover:text-white hover:border-[#0F62FE] transition-colors">
      {label} →
    </a>
  );
}

function AmlaCheck({ field, label, form, setForm }) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`amla-check-${field}`}>
      <input type="checkbox" className="mt-1" checked={!!form[field]}
             onChange={e => setForm({ ...form, [field]: e.target.checked })} />
      <span>{label}</span>
    </label>
  );
}

function SummaryCard({ label, value, tone = "neutral", testid }) {
  const tones = {
    neutral: "border-border",
    green:   "border-[#24A148]",
    yellow:  "border-[#F1C21B]",
    red:     "border-[#DA1E28]",
  };
  return (
    <div className={`border ${tones[tone]} p-4`} data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-3xl font-semibold mt-1">{value}</div>
    </div>
  );
}



// ----------------- eKYC -----------------
const EKYC_STATUS = {
  not_started: { label: "Not started", color: "bg-[#E0E0E0] text-[#404040]" },
  in_progress: { label: "In progress", color: "bg-[#F1C21B] text-[#3D3300]" },
  verified:    { label: "Verified",    color: "bg-[#24A148] text-white" },
  failed:      { label: "Failed",      color: "bg-[#DA1E28] text-white" },
};

const EKYC_METHODS = [
  "Physical (in-person NRIC sighting)",
  "Video call verification",
  "MyKad card reader",
  "Third-party portal",
  "Other",
];

function EkycTab({ client, onSaved }) {
  const [editing, setEditing] = useState(null);   // { party, index, person }
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const persons = [
    ...(client.directors || []).map((p, i) => ({ party: "director", index: i, person: p, role: p.role || "Director" })),
    ...(client.shareholders || []).map((p, i) => ({ party: "shareholder", index: i, person: p, role: "Shareholder" })),
    ...(client.secretaries || []).map((p, i) => ({ party: "secretary", index: i, person: p, role: "Company Secretary" })),
  ];

  const counts = persons.reduce((acc, p) => {
    const s = p.person.ekyc?.status || "not_started";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, { not_started: 0, in_progress: 0, verified: 0, failed: 0 });

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      status: row.person.ekyc?.status || "not_started",
      method: row.person.ekyc?.method || "",
      verified_by: row.person.ekyc?.verified_by || "",
      verified_date: row.person.ekyc?.verified_date || "",
      expires_at: row.person.ekyc?.expires_at || "",
      notes: row.person.ekyc?.notes || "",
      nric_front_doc_id: row.person.ekyc?.nric_front_doc_id || "",
      nric_back_doc_id: row.person.ekyc?.nric_back_doc_id || "",
      selfie_doc_id: row.person.ekyc?.selfie_doc_id || "",
      pep_status: row.person.ekyc?.pep_status || "unknown",
      sanctions_screening: row.person.ekyc?.sanctions_screening || "not_done",
      adverse_media: row.person.ekyc?.adverse_media || "not_done",
      screening_date: row.person.ekyc?.screening_date || "",
      screening_notes: row.person.ekyc?.screening_notes || "",
    });
  };

  const uploadKycFile = async (field, file) => {
    if (!file) return;
    const token = localStorage.getItem("cosec_token");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "ekyc");
    try {
      const r = await fetch(`${API}/clients/${client.id}/documents`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setForm(f => ({ ...f, [field]: data.id }));
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(String(e.message || e).slice(0, 200));
    }
  };

  const openDoc = (id) => {
    if (!id) return;
    const token = localStorage.getItem("cosec_token");
    window.open(`${API}/documents/${id}/download?auth=${token}`, "_blank");
  };

  const downloadReport = () => {
    const token = localStorage.getItem("cosec_token");
    window.open(`${API}/clients/${client.id}/ekyc-report/pdf?auth=${token}`, "_blank");
  };

  const [reminding, setReminding] = useState(false);
  const [sendingOne, setSendingOne] = useState(null); // key `${party}-${index}` while sending

  const bulkRemind = async () => {
    const notVerified = persons.filter(r => (r.person.ekyc?.status || "not_started") !== "verified");
    if (notVerified.length === 0) {
      return toast.info("All persons already verified — nothing to remind");
    }
    if (!window.confirm(`Email ${notVerified.length} person(s) to send NRIC + selfie? Verified persons will be skipped.`)) return;
    setReminding(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/ekyc/remind`, {});
      toast.success(`Sent ${data.sent} · Skipped ${data.skipped_no_email} no-email · Failed ${data.failed}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send reminders");
    } finally { setReminding(false); }
  };

  const remindOne = async (row) => {
    if (!row.person.email) {
      return toast.error(`${row.person.name || "This person"} has no email on file`);
    }
    const key = `${row.party}-${row.index}`;
    setSendingOne(key);
    try {
      const { data } = await api.post(`/clients/${client.id}/ekyc/remind-one`, {
        party: row.party, index: row.index,
      });
      toast.success(`eKYC link sent to ${data.recipient}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally { setSendingOne(null); }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const key = editing.party === "director" ? "directors"
                : editing.party === "shareholder" ? "shareholders" : "secretaries";
      const list = [...(client[key] || [])];
      list[editing.index] = { ...list[editing.index], ekyc: { ...form } };
      await api.put(`/clients/${client.id}`, { ...client, [key]: list });
      toast.success("eKYC updated");
      setEditing(null); setForm(null);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
          <SummaryCard label="Total persons" value={persons.length} tone="neutral" />
          <SummaryCard label="Verified" value={counts.verified} tone="green" testid="ekyc-verified-count" />
          <SummaryCard label="In progress" value={counts.in_progress} tone="yellow" />
          <SummaryCard label="Not started" value={counts.not_started + counts.failed} tone="red" />
        </div>
        <div className="flex items-center gap-2 ml-4">
          <Button variant="outline" className="rounded-sm h-10" onClick={bulkRemind}
                  disabled={reminding} data-testid="ekyc-bulk-remind-btn">
            <BellRinging size={16} weight="bold" className="mr-2" />
            {reminding ? "Sending…" : "Remind Not Verified"}
          </Button>
          <Button variant="outline" className="rounded-sm h-10" onClick={downloadReport}
                  data-testid="ekyc-report-pdf-btn">
            <FileText size={16} weight="bold" className="mr-2" /> eKYC Report PDF
          </Button>
        </div>
      </div>

      <div className="border border-border" data-testid="ekyc-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border">
              <Th>Name</Th><Th>Role</Th><Th>NRIC/Passport</Th><Th>Status</Th><Th>Attachments</Th><Th>Verified</Th><Th>Expires</Th><Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {persons.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-10 text-sm text-muted-foreground">
                Add directors, shareholders or secretaries first to run eKYC on them.
              </td></tr>
            )}
            {persons.map((row, i) => {
              const st = row.person.ekyc?.status || "not_started";
              const meta = EKYC_STATUS[st] || EKYC_STATUS.not_started;
              const ek = row.person.ekyc || {};
              return (
                <tr key={`${row.party}-${row.index}`} className="data-row border-b border-border">
                  <td className="px-6 py-3">{row.person.name || "—"}</td>
                  <td className="px-6 py-3 text-xs">{row.role}</td>
                  <td className="px-6 py-3 font-mono text-xs">{row.person.ic_no || "—"}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest ${meta.color}`}
                          data-testid={`ekyc-status-${i}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-1.5">
                      <EkycThumbnail docId={ek.nric_front_doc_id} label="F" title="NRIC Front" />
                      <EkycThumbnail docId={ek.nric_back_doc_id}  label="B" title="NRIC Back"  />
                      <EkycThumbnail docId={ek.selfie_doc_id}     label="S" title="Selfie"     />
                    </div>
                  </td>
                  <td className="px-6 py-3 font-mono text-xs">{row.person.ekyc?.verified_date ? fmtMY(row.person.ekyc.verified_date) : "—"}</td>
                  <td className="px-6 py-3 font-mono text-xs">{row.person.ekyc?.expires_at ? fmtMY(row.person.ekyc.expires_at) : "—"}</td>
                  <td className="px-6 py-3 text-right space-x-1">
                    <Button size="sm" variant="outline" className="rounded-sm h-8"
                            onClick={() => remindOne(row)}
                            disabled={sendingOne === `${row.party}-${row.index}` || !row.person.email}
                            title={row.person.email ? `Send eKYC link to ${row.person.email}` : "No email on file"}
                            data-testid={`ekyc-send-link-${i}`}>
                      <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
                      {sendingOne === `${row.party}-${row.index}` ? "Sending…" : "Send link"}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-8"
                            onClick={() => openEdit(row)} data-testid={`ekyc-update-${i}`}>
                      <ShieldCheck size={14} weight="bold" className="mr-1.5" /> Update
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={o => { if (!o) { setEditing(null); setForm(null); } }}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle>eKYC — {editing?.person?.name || "—"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <F label="Status" full>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="ekyc-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(EKYC_STATUS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Method" full>
                <Select value={form.method || "__none__"} onValueChange={v => setForm({ ...form, method: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm" data-testid="ekyc-method-select"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {EKYC_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Verified by (name)">
                <Input value={form.verified_by} onChange={e => setForm({ ...form, verified_by: e.target.value })}
                       className="rounded-sm" data-testid="ekyc-verified-by" />
              </F>
              <F label="Verified date (YYYY-MM-DD)">
                <Input value={form.verified_date} onChange={e => setForm({ ...form, verified_date: e.target.value })}
                       placeholder="2026-02-15" className="rounded-sm" data-testid="ekyc-verified-date" />
              </F>
              <F label="Expires (YYYY-MM-DD)" full>
                <Input value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })}
                       placeholder="2027-02-15" className="rounded-sm" data-testid="ekyc-expires-at" />
              </F>
              <F label="Notes" full>
                <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                          className="rounded-sm" data-testid="ekyc-notes" />
              </F>
              <div className="col-span-2 border-t border-border pt-4 mt-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  Attachments (kept in portal for audit trail)
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <KycFileSlot label="NRIC (Front)" docId={form.nric_front_doc_id}
                               onUpload={(f) => uploadKycFile("nric_front_doc_id", f)}
                               onView={() => openDoc(form.nric_front_doc_id)}
                               onClear={() => setForm(v => ({ ...v, nric_front_doc_id: "" }))}
                               testid="ekyc-nric-front" />
                  <KycFileSlot label="NRIC (Back)" docId={form.nric_back_doc_id}
                               onUpload={(f) => uploadKycFile("nric_back_doc_id", f)}
                               onView={() => openDoc(form.nric_back_doc_id)}
                               onClear={() => setForm(v => ({ ...v, nric_back_doc_id: "" }))}
                               testid="ekyc-nric-back" />
                  <KycFileSlot label="Selfie" docId={form.selfie_doc_id}
                               onUpload={(f) => uploadKycFile("selfie_doc_id", f)}
                               onView={() => openDoc(form.selfie_doc_id)}
                               onClear={() => setForm(v => ({ ...v, selfie_doc_id: "" }))}
                               testid="ekyc-selfie" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setEditing(null); setForm(null); }}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={save} disabled={saving}
                    data-testid="ekyc-save-btn">
              {saving ? "Saving…" : "Save eKYC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EkycThumbnail({ docId, label, title }) {
  const [failed, setFailed] = useState(false);
  if (!docId) {
    return (
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-dashed border-border text-[10px] font-mono text-muted-foreground"
            title={`${title}: not uploaded`}>{label}</span>
    );
  }
  const token = localStorage.getItem("cosec_token");
  const url = `${API}/documents/${docId}/download?auth=${token}`;
  const open = () => window.open(url, "_blank");
  if (failed) {
    return (
      <button type="button" onClick={open}
              className="inline-flex items-center justify-center w-8 h-8 rounded-sm bg-[#111] text-white text-[9px] font-mono hover:bg-[#0F62FE]"
              title={`${title}: click to view (PDF)`}>PDF</button>
    );
  }
  return (
    <button type="button" onClick={open} className="w-8 h-8 rounded-sm overflow-hidden border border-border hover:ring-2 hover:ring-[#0F62FE]"
            title={`${title}: click to view`}>
      <img src={url} alt={title}
           className="w-full h-full object-cover"
           onError={() => setFailed(true)} />
    </button>
  );
}

function KycFileSlot({ label, docId, onUpload, onView, onClear, testid }) {
  const ref = useRef(null);
  return (
    <div className="border border-border p-3 rounded-sm" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{label}</div>
      <input ref={ref} type="file" accept="image/*,application/pdf" hidden
             onChange={e => { if (e.target.files?.[0]) onUpload(e.target.files[0]); if (ref.current) ref.current.value = ""; }} />
      {docId ? (
        <div className="space-y-2">
          <div className="text-xs text-[#24A148] font-mono truncate" title={docId}>Uploaded ✓</div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs px-2" onClick={onView}>View</Button>
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs px-2" onClick={() => ref.current?.click()}>Replace</Button>
            <Button size="sm" variant="ghost" className="text-[#DA1E28] h-7 text-xs px-2" onClick={onClear}>Clear</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="rounded-sm h-8 w-full"
                onClick={() => ref.current?.click()}>
          <UploadSimple size={13} weight="bold" className="mr-1.5" /> Upload
        </Button>
      )}
    </div>
  );
}



// helpers
function F({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
const Th = ({ children, className = "" }) =>
  <th className={`px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal ${className}`}>{children}</th>;
